import type { AuditIssue, QaDefect } from '@360t/shared';
import { SeverityBadge } from './SeverityBadge';

type Entry = {
  key: string;
  severity: AuditIssue['severity'];
  meta: string;
  title: string;
  detail: string;
  evidence?: string[];
};

export const auditIssuesToEntries = (issues: (AuditIssue & { id?: string })[]): Entry[] =>
  issues.map((i, idx) => ({
    key: i.id ?? `${i.category}-${idx}`,
    severity: i.severity,
    meta: i.category,
    title: i.title,
    detail: i.recommendation,
    evidence: i.evidence,
  }));

export const qaDefectsToEntries = (defects: (QaDefect & { id?: string })[]): Entry[] =>
  defects.map((d, idx) => ({
    key: d.id ?? `${d.category}-${idx}`,
    severity: d.severity,
    meta: `${d.category} · ${new URL(d.pageUrl).pathname || '/'}`,
    title: d.description,
    detail: d.suggestedFix,
    evidence: d.evidence,
  }));

export function IssueList({ entries, emptyText }: { entries: Entry[]; emptyText: string }) {
  if (entries.length === 0) {
    return <p className="rounded-lg bg-good/5 px-4 py-3 text-sm text-good">{emptyText}</p>;
  }

  return (
    <ul className="space-y-3">
      {entries.map((e) => (
        <li
          key={e.key}
          className={`rounded-lg border border-line border-l-[3px] bg-white p-4 ${
            e.severity === 'critical' ? 'border-l-poor' : e.severity === 'warning' ? 'border-l-average' : 'border-l-accent'
          }`}
        >
          <div className="mb-1.5 flex items-center justify-between gap-3">
            <SeverityBadge severity={e.severity} />
            <span className="truncate text-[11px] uppercase tracking-wide text-slate-400">{e.meta}</span>
          </div>
          <h4 className="text-sm font-semibold text-ink">{e.title}</h4>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">{e.detail}</p>
          {e.evidence && e.evidence.length > 0 && (
            <ul className="mt-2 space-y-0.5 border-l border-line pl-3 font-mono text-[11px] text-slate-400">
              {e.evidence.slice(0, 4).map((ev) => (
                <li key={ev} className="break-all">
                  {ev}
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
}
